import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell,
  BarChart, Bar, LineChart, Line, ComposedChart, ReferenceLine,
  RadialBarChart, RadialBar, Legend
} from 'recharts';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  ChartBarIcon,
  UserPlusIcon,
  GlobeAltIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  ServerIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon as UserGroupIconSolid,
  ShieldCheckIcon as ShieldCheckIconSolid,
  CloudIcon as CloudIconSolid,
  BoltIcon as BoltIconSolid,
  HomeIcon as HomeIconSolid
} from '@heroicons/react/24/solid';

import { useAdminData } from '../../context/AdminDataContext';
import { useUser } from '../../context/UserContext';
import axiosInstance from '../api/axiosInstance';
import BreadcrumbNavigation from './BreadcrumbNavigation';

// Design System Components
import Button from '../ui/Button';
import Card, { CardHeader } from '../ui/Card';
import { StatusBadge } from '../ui/Table';
import logger from '../../utils/logger';
// TrialStatusWidget removed — trial indicator shown in sidebar only

const CHART_COLORS = ['#6B7280', '#9CA3AF', '#D1D5DB', '#4B5563', '#374151', '#1F2937', '#111827', '#E5E7EB'];

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.dataKey}:</span>
            <span className="font-semibold text-gray-900 dark:text-white">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Performance Score Component
const PerformanceGauge = ({ score, title, className = "" }) => {
  const data = [
    { name: 'Score', value: score, fill: score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444' },
    { name: 'Remaining', value: 100 - score, fill: '#E5E7EB' }
  ];

  return (
    <div className={`text-center ${className}`}>
      <ResponsiveContainer width="100%" height={120}>
        <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" data={data}>
          <RadialBar dataKey="value" cornerRadius={10} fill="#E5E7EB" />
          <RadialBar dataKey="value" cornerRadius={10} fill={data[0].fill} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex items-center justify-center">
        <div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{score}%</div>
          <div className="text-xs text-muted-foreground">{title}</div>
        </div>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyChartState = ({ message = "No data available", icon: Icon = ChartBarIcon }) => (
  <div className="flex flex-col items-center justify-center h-full py-12">
    <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-gray-400" />
    </div>
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

const ModernStatCard = ({ title, value, change, icon, subtitle }) => {
  const getGrowthIcon = (change) => {
    if (change > 0) return <ArrowTrendingUpIcon className="w-3.5 h-3.5" />;
    if (change < 0) return <ArrowTrendingDownIcon className="w-3.5 h-3.5" />;
    return <ClockIcon className="w-3.5 h-3.5" />;
  };

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-[#1a1a1a]">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
          {React.cloneElement(icon, {
            className: 'w-5 h-5 text-gray-400 dark:text-gray-500'
          })}
        </div>
        {change !== 0 && (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium ${
            change > 0
              ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
          }`}>
            {getGrowthIcon(change)}
            {Math.abs(change)}%
          </span>
        )}
      </div>

      <div className="space-y-0.5">
        <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</h3>
        <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
      </div>
    </div>
  );
};

const ModernDashboard = ({ showNotification }) => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const adminDataContext = useAdminData();
  const { getUsers, getStorage, getDashboard, isLoading } = adminDataContext;
  const [dashboardData, setDashboardData] = useState(null);
  const [businessSummary, setBusinessSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Make all API calls in parallel for better performance
      // Use limit=1 for users endpoint since we only need the total count from pagination
      const [dashboardResponse, profileResponse, usersResponse, storageResponse, activityResponse, domainsResponse] = await Promise.all([
        axiosInstance.get('/api/business/dashboard').catch(() => ({ data: { success: false } })),
        axiosInstance.get('/api/business/profile/summary').catch(() => ({ data: { success: false } })),
        axiosInstance.get('/api/business/users?limit=1').catch(() => ({ data: { success: false } })), // Only fetch 1 user, we need the total from pagination
        axiosInstance.get('/api/business/storage/analytics?timeRange=30d').catch(() => ({ data: { success: false } })),
        axiosInstance.get('/api/business/activity/recent').catch(() => ({ data: { success: false, activities: [] } })),
        axiosInstance.get('/api/business/domains?limit=1').catch(() => ({ data: { success: false } })) // Fetch domains count
      ]);

      // Handle business profile summary
      if (profileResponse.data.success) {
        setBusinessSummary(profileResponse.data.data);
      }

      // Get actual user count from pagination metadata
      let totalUsers = 0;
      if (usersResponse.data.success && usersResponse.data.pagination) {
        totalUsers = usersResponse.data.pagination.total || 0;
      }

      // Get actual domains count from pagination metadata
      let totalDomains = 0;
      let activeDomains = 0;
      if (domainsResponse.data.success) {
        if (domainsResponse.data.pagination) {
          totalDomains = domainsResponse.data.pagination.total || 0;
        }
        // Count active/verified domains
        if (domainsResponse.data.domains) {
          activeDomains = domainsResponse.data.domains.filter(d => d.verified || d.status === 'active').length;
        }
      }

      // Log storage response to see actual data structure

      // Only use real API data - no mock data
      const dashboardData = dashboardResponse.data?.dashboard || {};
      
      // Handle user data - only use real data from API
      const usersInfo = {
        total: totalUsers || 0,
        active: usersResponse.data?.activeUsers || Math.floor((totalUsers || 0) * 0.7),
        change: dashboardData?.userGrowth || 0
      };
      
      // Handle storage data - only use real data from API
      let storageInfo = {
        used: 0,
        total: 0,
        change: 0
      };
      
      if (storageResponse.data.success && storageResponse.data.storage) {
        const storageData = storageResponse.data.storage;

        // The analytics API often returns usedStorage=0 while the real usage
        // lives in the per-type fields, and reports the QUOTA under different
        // keys (quota / storageLimit / …) — with `totalStorage` sometimes
        // mirroring "used". Mirror the Storage page's robust resolution so the
        // dashboard card doesn't show "0 B of 652 MB" while storage is in use.
        const emailStorage = Number(storageData.emailStorage) || 0;
        const fileStorage = Number(storageData.fileStorage) || 0;
        const otherStorage = Number(storageData.otherStorage) || 0;
        const calcUsed = emailStorage + fileStorage + otherStorage;
        const used = (Number(storageData.usedStorage) > 0)
          ? Number(storageData.usedStorage)
          : (Number(storageData.totalUsed) > 0 ? Number(storageData.totalUsed) : calcUsed);

        // Prefer explicit quota fields; otherwise use totalStorage only if it's
        // meaningfully larger than `used` (else it's just echoing usage).
        const quotaCandidates = [
          storageData.quota, storageData.storageLimit, storageData.maxStorage,
          storageData.allocatedStorage, storageData.storageQuota,
          storageData.totalLimit, storageData.limit
        ].map(Number).filter(v => Number.isFinite(v) && v > 0);
        let total = quotaCandidates[0] || 0;
        if (!total) {
          const ts = Number(storageData.totalStorage);
          total = (Number.isFinite(ts) && ts > used * 1.1) ? ts : (ts || used);
        }

        storageInfo = { used, total, change: Number(storageData.storageGrowth) || 0 };
      }
        
      // Handle recent activity data - only real data
      let recentActivity = [];
      if (activityResponse.data.success && activityResponse.data.activities) {
        recentActivity = activityResponse.data.activities;
      }
      // No fallback activity - show empty if no real data

      // Calculate storage percentage only if we have data
      const storagePercentage = storageInfo.total > 0 ? Math.round((storageInfo.used / storageInfo.total) * 100) : 0;
      
      // Set dashboard data - only use real API data
      setDashboardData({
        users: usersInfo,
        storage: storageInfo,
        domains: {
          total: totalDomains || 0,
          active: activeDomains || 0,
          change: dashboardData?.domainGrowth || 0
        },
        security: {
          score: dashboardData?.security?.score || 0,
          change: dashboardData?.security?.change || 0,
          incidents: dashboardData?.security?.incidents || []
        },
        sessions: {
          active: dashboardData?.sessions?.active || 0,
          change: dashboardData?.sessions?.change || 0
        },
        charts: {
          // User activity from dashboard API
          userActivity: dashboardData?.charts?.userActivity || [],
          
          // Storage breakdown - only show if we have real data
          storage: storageInfo.used > 0 ? [
            { 
              name: 'Email Storage', 
              value: Math.round((storageResponse.data?.storage?.emailStorage || 0) / storageInfo.total * 100), 
              color: '#F97316' 
            },
            { 
              name: 'File Storage', 
              value: Math.round((storageResponse.data?.storage?.fileStorage || 0) / storageInfo.total * 100), 
              color: '#3B82F6' 
            },
            { 
              name: 'System Data', 
              value: Math.round((storageResponse.data?.storage?.systemStorage || 0) / storageInfo.total * 100), 
              color: '#10B981' 
            },
            { 
              name: 'Available', 
              value: Math.max(0, 100 - storagePercentage), 
              color: '#E5E7EB' 
            }
          ] : [],
          
          // Performance metrics from dashboard API
          performance: dashboardData?.charts?.performance || [],
          
          // Traffic sources from dashboard API  
          trafficSources: dashboardData?.charts?.trafficSources || [],
          
          // Department usage from dashboard API
          departmentUsage: dashboardData?.charts?.departmentUsage || [],
          
          // Recent activity - real data only
          recentActivity: recentActivity
        }
      });

      // Set business summary only from real API data
      if (profileResponse.data.success) {
        setBusinessSummary(profileResponse.data.data);
      }
      
    } catch (error) {
      logger.error('Error fetching dashboard data:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard data');
      
      // Set empty dashboard data on error - no mock data
      setDashboardData({
        users: { total: 0, active: 0, change: 0 },
        storage: { used: 0, total: 0, change: 0 },
        domains: { total: 0, active: 0, change: 0 },
        security: { score: 0, change: 0, incidents: [] },
        sessions: { active: 0, change: 0 },
        charts: {
          userActivity: [],
          storage: [],
          performance: [],
          trafficSources: [],
          departmentUsage: [],
          recentActivity: []
        }
      });
    } finally {
      setLoading(false);
    }
  }, []); // Remove showNotification dependency to prevent re-fetches

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]); // Depend on fetchDashboardData which is now stable

  // Separate refresh function to avoid infinite loops
  const refreshDashboard = () => {
    fetchDashboardData();
  };
  
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-[#111111] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                  <HomeIconSolid className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Business overview and analytics</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-[#111111]">
          <div className="text-center">
            <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-[#111111] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800">
          <div className="px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                <HomeIconSolid className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Business overview and analytics</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-[#111111]">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="w-16 h-16 bg-gray-100 dark:bg-[#222222] rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Error Loading Dashboard</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button
              onClick={refreshDashboard}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatStorageSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-[#111111]">
      {/* Clean Header */}
      <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
              <HomeIconSolid className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Dashboard Overview
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Welcome back! Here's what's happening with your business today.
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={refreshDashboard}
              disabled={loading}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
            >
              <ArrowPathIcon className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-[#111111]">
        <div className="max-w-7xl mx-auto space-y-4">

          {/* Key Metrics */}
          <div className="grid lg:grid-cols-3 gap-4">
            <ModernStatCard
              title="Total Users"
              value={dashboardData?.users?.total?.toLocaleString() || "0"}
              change={dashboardData?.users?.change || 0}
              icon={<UserGroupIconSolid />}
              subtitle={`${dashboardData?.users?.active || 0} active users`}
            />

            <ModernStatCard
              title="Total Domains"
              value={dashboardData?.domains?.total?.toLocaleString() || "0"}
              change={dashboardData?.domains?.change || 0}
              icon={<GlobeAltIcon />}
              subtitle={`${dashboardData?.domains?.active || 0} verified domains`}
            />

            <ModernStatCard
              title="Active Sessions"
              value={dashboardData?.sessions?.active?.toLocaleString() || "0"}
              change={dashboardData?.sessions?.change || 0}
              icon={<BoltIconSolid />}
              subtitle="Currently online"
            />
          </div>

          {/* Storage Card - Only show if there's actual data */}
          {(dashboardData?.storage?.used > 0 || dashboardData?.storage?.total > 0) && (
            <div className="grid lg:grid-cols-1 gap-4">
              <ModernStatCard
                title="Storage Used"
                value={formatStorageSize(dashboardData?.storage?.used || 0)}
                change={dashboardData?.storage?.change || 0}
                icon={<CloudIconSolid />}
                subtitle={(() => {
                  const used = dashboardData?.storage?.used || 0;
                  const total = dashboardData?.storage?.total || 0;
                  const totalLabel = `of ${formatStorageSize(total)} total capacity`;
                  if (total <= 0) return totalLabel;
                  const pct = (used / total) * 100;
                  const pctLabel = pct > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`;
                  return `${pctLabel} of ${formatStorageSize(total)} used`;
                })()}
              />
            </div>
          )}

          {/* Quick Actions */}
          <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                <BoltIconSolid className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Frequently used administrative tasks</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <button
                onClick={() => navigate('/users')}
                className="flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <UserPlusIcon className="w-4 h-4" />
                <span>Add User</span>
              </button>

              <button
                onClick={() => navigate('/domains')}
                className="flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <GlobeAltIcon className="w-4 h-4" />
                <span>Manage Domains</span>
              </button>

              <button
                onClick={() => navigate('/settings')}
                className="flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <Cog6ToothIcon className="w-4 h-4" />
                <span>Settings</span>
              </button>

              <button
                onClick={() => navigate('/reports')}
                className="flex items-center gap-2 px-3 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <DocumentTextIcon className="w-4 h-4" />
                <span>View Reports</span>
              </button>
            </div>
          </div>

          {/* System Status */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                  <ServerIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">System Health</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">All systems operational</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-white">Mail Server</span>
                  </div>
                  <span className="px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs font-medium rounded-md">
                    Operational
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-white">Authentication</span>
                  </div>
                  <span className="px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs font-medium rounded-md">
                    Operational
                  </span>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-xs font-medium text-gray-900 dark:text-white">Database</span>
                  </div>
                  <span className="px-2 py-0.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs font-medium rounded-md">
                    Operational
                  </span>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                  <BellIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Latest system events</p>
                </div>
              </div>

              <div className="space-y-2">
                {dashboardData?.charts?.recentActivity && dashboardData.charts.recentActivity.length > 0 ? (
                  dashboardData.charts.recentActivity.slice(0, 5).map((activity, index) => {
                    const getActivityColor = (type) => {
                      switch (type) {
                        case 'user_created': return 'from-blue-400 to-blue-500';
                        case 'domain_verified': return 'from-green-400 to-green-500';
                        case 'backup_completed': return 'from-orange-400 to-orange-500';
                        case 'login': return 'from-purple-400 to-purple-500';
                        default: return 'from-gray-400 to-gray-500';
                      }
                    };

                    const getActivityIcon = (type) => {
                      switch (type) {
                        case 'user_created': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
                        case 'domain_verified': return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
                        case 'backup_completed': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400';
                        case 'login': return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
                        default: return 'bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-400';
                      }
                    };

                    const formatTime = (timestamp) => {
                      const now = new Date();
                      const activityTime = new Date(timestamp);
                      const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));

                      if (diffInMinutes < 1) return 'Just now';
                      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
                      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
                      return `${Math.floor(diffInMinutes / 1440)}d ago`;
                    };

                    return (
                      <div
                        key={activity.id || index}
                        className="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-[#222222] rounded-lg transition-colors duration-150 border border-transparent hover:border-gray-200 dark:hover:border-gray-800"
                      >
                        <div className={`w-8 h-8 ${getActivityIcon(activity.type)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <div className="w-2 h-2 rounded-full bg-current"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {activity.description}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {activity.details?.email || activity.user}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-500 whitespace-nowrap">
                          {formatTime(activity.timestamp)}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-[#222222] rounded-full flex items-center justify-center mb-3">
                      <BellIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No recent activity</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500">Activity will appear here once users start using the system</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Enhanced Charts Grid - Only show if there's data */}
          {(dashboardData?.charts?.userActivity?.length > 0 || dashboardData?.charts?.storage?.length > 0) && (
            <div className="grid lg:grid-cols-3 gap-4">

              {/* User Activity Trends - Enhanced */}
              {dashboardData?.charts?.userActivity?.length > 0 && (
                <div className="lg:col-span-2">
                  <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                          <ChartBarIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">User Engagement Analytics</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Comprehensive user activity and retention metrics</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          <div className="w-2.5 h-2.5 bg-gray-600 rounded-full"></div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Total Users</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2.5 h-2.5 bg-gray-400 rounded-full"></div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">Active Users</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2.5 h-2.5 bg-gray-500 rounded-full"></div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">New Users</span>
                        </div>
                      </div>
                    </div>

                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={dashboardData.charts.userActivity}>
                      <defs>
                        <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6B7280" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#6B7280" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="activeGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#9CA3AF" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#9CA3AF" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                      <XAxis
                        dataKey="month"
                        fontSize={11}
                        tickMargin={8}
                        stroke="#6B7280"
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        yAxisId="users"
                        fontSize={11}
                        tickMargin={8}
                        stroke="#6B7280"
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        yAxisId="retention"
                        orientation="right"
                        fontSize={11}
                        tickMargin={8}
                        stroke="#6B7280"
                        axisLine={false}
                        tickLine={false}
                        domain={[80, 100]}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Area
                        yAxisId="users"
                        type="monotone"
                        dataKey="users"
                        stroke="#6B7280"
                        fillOpacity={1}
                        fill="url(#userGradient)"
                        strokeWidth={2}
                        name="Total Users"
                      />
                      <Area
                        yAxisId="users"
                        type="monotone"
                        dataKey="active"
                        stroke="#9CA3AF"
                        fillOpacity={1}
                        fill="url(#activeGradient)"
                        strokeWidth={2}
                        name="Active Users"
                      />
                      <Bar
                        yAxisId="users"
                        dataKey="newUsers"
                        fill="#4B5563"
                        name="New Users"
                        radius={[2, 2, 0, 0]}
                      />
                      <Line
                        yAxisId="retention"
                        type="monotone"
                        dataKey="retention"
                        stroke="#6B7280"
                        strokeWidth={3}
                        dot={{ fill: '#6B7280', strokeWidth: 2, r: 4 }}
                        name="Retention %"
                      />
                    </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {/* Enhanced Storage Breakdown */}
              {dashboardData?.charts?.storage?.length > 0 && (
                <div>
                  <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                        <CloudIconSolid className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Storage Breakdown</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Usage by category</p>
                      </div>
                    </div>

                <div className="relative">
                  <div className="h-40 mb-3">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={dashboardData.charts.storage}
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          startAngle={90}
                          endAngle={-270}
                        >
                          {(dashboardData?.charts?.storage || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value, name) => [`${value}%`, name]}
                          labelStyle={{ color: '#374151' }}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Storage Legend */}
                  <div className="space-y-2">
                    {dashboardData.charts.storage.map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-gray-700">{item.name}</span>
                        </div>
                        <span className="font-medium text-foreground">{item.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Performance Metrics - Only show if there's data */}
          {(dashboardData?.charts?.performance?.length > 0 || dashboardData?.charts?.departmentUsage?.length > 0) && (
            <div className="grid lg:grid-cols-2 gap-4">
              {dashboardData?.charts?.performance?.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                      <CheckCircleIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">System Performance</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Key performance indicators vs targets</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {dashboardData.charts.performance?.map((metric, index) => (
                      <div key={index} className="relative">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{metric.metric}</span>
                          <div className="flex items-center space-x-1.5">
                            <span className="text-xs font-bold text-gray-900 dark:text-white">{metric.score}%</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">/ {metric.target}%</span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-[#222222] rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full transition-all duration-500 bg-gray-500 dark:bg-gray-400"
                            style={{ width: `${Math.min(metric.score, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {dashboardData?.charts?.departmentUsage?.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                      <DocumentTextIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Department Usage</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Usage statistics by department</p>
                    </div>
                  </div>

                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dashboardData.charts.departmentUsage} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                        <XAxis type="number" fontSize={11} stroke="#6B7280" axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="dept" fontSize={11} stroke="#6B7280" axisLine={false} tickLine={false} />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Bar dataKey="users" fill="#6B7280" radius={[0, 2, 2, 0]} name="Users" />
                        <Bar dataKey="storage" fill="#9CA3AF" radius={[0, 2, 2, 0]} name="Storage (GB)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Traffic Sources - Only show if there's data */}
          {dashboardData?.charts?.trafficSources?.length > 0 && (
            <div className="grid lg:grid-cols-1 gap-4">
              <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-[#1a1a1a]">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                    <GlobeAltIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Access Sources</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">How users access the system</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {dashboardData.charts.trafficSources.map((source, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                        />
                        <div>
                          <p className="text-xs font-medium text-gray-900 dark:text-white">{source.source}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{source.users} users</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900 dark:text-white">{source.percentage}%</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModernDashboard;